import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { NewsInput } from '../../lib/types'

/**
 * Новости (🔶 /api/news). Чтение — публичное (токен только гейтит черновики admin).
 * Лента курсорная — { items (без body), nextCursor }. useNews разворачивает
 * только первую страницу (home.tsx — превью-виджет, без пагинации).
 */
export function useNews() {
  return useQuery({
    queryKey: ['news'],
    queryFn: () => api.news().then((r) => r.items),
  })
}

/**
 * Полная пагинация по cursor (news.tsx). `data` — плоский NewsItem[] (страницы
 * склеены селектором). Ключ ['news', 'infinite'] матчится префиксом ['news'] в
 * invalidateQueries useUpdateNews/useDeleteNews ниже — их трогать не надо.
 */
export function useInfiniteNews() {
  return useInfiniteQuery({
    queryKey: ['news', 'infinite'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => api.news(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    select: (data) => data.pages.flatMap((p) => p.items),
  })
}

/** Деталь новости — с полем body (GET /news/:id). Черновик → 404 для не-admin. */
export function useNewsItem(id: string) {
  return useQuery({
    queryKey: ['news', id],
    queryFn: () => api.newsItem(id),
    enabled: !!id,
  })
}

/** Правка новости — admin/hr (иначе 403). Инвалидация ['news'] покрывает и список, и деталь. */
export function useUpdateNews(id: string, onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<NewsInput>) => api.updateNews(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news'] })
      onDone?.()
    },
  })
}

/** Удаление новости — admin/hr; каскадно сносит вложения. */
export function useDeleteNews(onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deleteNews(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['news'] })
      onDone?.()
    },
  })
}
